import { useAtomValue } from 'jotai';
import { RefObject, useEffect, useRef } from 'react';

import { v2ZoomAtom } from '../../store/v2/viewer.atom';
import {
  clampV2Zoom,
  snapV2PinchZoom,
} from '../../components/v2/zoom.constants';

const getDistance = (touches: TouchList) => {
  const [first, second] = [touches[0], touches[1]];
  return Math.hypot(
    second.clientX - first.clientX,
    second.clientY - first.clientY,
  );
};

const getMidpoint = (touches: TouchList) => {
  const [first, second] = [touches[0], touches[1]];
  return {
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  };
};

interface Props {
  /** 네이티브 스크롤 컨테이너(터치 이벤트 대상) */
  scrollRef: RefObject<HTMLElement>;
  /** 제스처 중 CSS transform 프리뷰를 적용할 요소 */
  transformTargetRef: RefObject<HTMLElement>;
  /** 제스처 종료 시 최종 배율을 커밋하는 앵커 줌 함수 */
  zoomAtPoint: (
    nextZoom: number,
    clientX: number,
    clientY: number,
    pan?: { x: number; y: number },
    options?: { immediate?: boolean },
  ) => void;
}

/**
 * 모바일 핀치줌.
 * 제스처 중에는 리렌더 없이 CSS transform(핀치 중점 origin)으로만
 * 미리보기하고, 손가락을 뗄 때 한 번만 실제 배율을 커밋한다.
 * 커밋 시 스크롤 보정은 useAnchoredZoom이 담당한다.
 */
const usePinchZoom = ({
  scrollRef,
  transformTargetRef,
  zoomAtPoint,
}: Props) => {
  const zoom = useAtomValue(v2ZoomAtom);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;

  useEffect(() => {
    const target = scrollRef.current;
    if (!target) {
      return;
    }

    let startDistance: number | null = null;
    let startZoom = 1;
    let startMidX = 0;
    let startMidY = 0;
    let nextZoom = 1;
    let panX = 0;
    let panY = 0;
    let rafId: number | null = null;

    const clearGestureStyle = () => {
      const el = transformTargetRef.current;
      if (el) {
        el.style.transform = '';
        el.style.transformOrigin = '';
        el.style.willChange = '';
      }
    };

    const handleTouchStart = (event: globalThis.TouchEvent) => {
      if (event.touches.length !== 2) {
        return;
      }

      /* 브라우저가 팬/스크롤로 제스처를 선점(touchcancel)하지 못하게 막는다 */
      event.preventDefault();

      startDistance = getDistance(event.touches);
      startZoom = zoomRef.current;
      nextZoom = startZoom;
      const midpoint = getMidpoint(event.touches);
      startMidX = midpoint.x;
      startMidY = midpoint.y;
      panX = 0;
      panY = 0;

      const el = transformTargetRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        el.style.transformOrigin = `${startMidX - rect.left}px ${
          startMidY - rect.top
        }px`;
        el.style.willChange = 'transform';
      }
    };

    const handleTouchMove = (event: globalThis.TouchEvent) => {
      if (event.touches.length !== 2 || startDistance === null) {
        return;
      }

      /* 브라우저 기본 핀치줌(페이지 확대)을 막는다 */
      event.preventDefault();

      const ratio = getDistance(event.touches) / startDistance;
      nextZoom = clampV2Zoom(startZoom * ratio);

      const midpoint = getMidpoint(event.touches);
      panX = midpoint.x - startMidX;
      panY = midpoint.y - startMidY;

      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          const el = transformTargetRef.current;
          if (el && startDistance !== null) {
            el.style.transform = `translate(${panX}px, ${panY}px) scale(${
              nextZoom / startZoom
            })`;
          }
        });
      }
    };

    const handleTouchEnd = (event: globalThis.TouchEvent) => {
      if (event.touches.length >= 2 || startDistance === null) {
        return;
      }
      startDistance = null;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      /* 커밋 전 원복해야 앵커 계산이 변형 전 레이아웃 기준으로 이뤄진다 */
      clearGestureStyle();

      if (nextZoom !== startZoom || panX !== 0 || panY !== 0) {
        /*
         * UX상 5% 단위(100% 근접 시 100%)로 스냅해 커밋한다.
         * 프리뷰 transform 원복 직후이므로 immediate 커밋으로
         * 이전 레이아웃이 한 프레임 노출되는 것을 막는다.
         */
        zoomAtPoint(
          snapV2PinchZoom(nextZoom),
          startMidX,
          startMidY,
          { x: panX, y: panY },
          { immediate: true },
        );
      }
    };

    target.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    target.addEventListener('touchmove', handleTouchMove, { passive: false });
    target.addEventListener('touchend', handleTouchEnd, { passive: true });
    target.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      clearGestureStyle();
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchmove', handleTouchMove);
      target.removeEventListener('touchend', handleTouchEnd);
      target.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [scrollRef, transformTargetRef, zoomAtPoint]);
};

export default usePinchZoom;
